import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Disclosure } from "@/components/Disclosure";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import { useState } from "react";
import { ModalLogsSincro } from "./components/ModalLogSincro";
import { TablaStockArticulos } from "./TablaSincroAlmacen";
import { Loading, QueryError } from "@/components/Loading/Loading";
import "./SincroAlmacen.css";

export function SincroAlmacenPage() {
	const [modalShown, setModalShown] = useState(false);
	const [almacen, setAlmacen] = useState<number>(1);
	const [syncFilter, setSyncFilter] = useState<"todos" | "no-sincronizados">("no-sincronizados");
	const [stock, setStock] = useState<"conStock" | "todos">("conStock");

	return (
		<div id="content" className="content" >
			<title>Piher | Sincronizar Almacén </title>
			<div className="row">
				<div className="col-lg-12">
					<div className="page-header">
						<h2>Panel de sincronización de almacén</h2>
						<ol className="breadcrumb">
							<li className="active">Sincronizar</li>
						</ol>
						<div style={{ float: "right" }}>
							<button className="btnPrimary" onClick={() => { setModalShown(true); }} type="button">
								Ver logs
							</button>
						</div>
					</div>
				</div>
			</div>

			<Disclosure style={{ marginBottom: 20 }}>
				<h4 className="panel-title panel-heading senalar">
					Filtro
				</h4>
				<FiltroSincro
					almacen={almacen}
					setAlmacen={setAlmacen}
					syncFilter={syncFilter}
					setSyncFilter={setSyncFilter}
					stock={stock}
					setStock={setStock}
				/>
			</Disclosure>

			<div className="table-responsive">
				<TablaStockArticulos
					idAlmacen={almacen}
					syncFilter={syncFilter}
					stock={stock}
				/>
			</div>

			<ModalLogsSincro show={modalShown} close={() => { setModalShown(false); }} />
		</div>
	);
}

interface FiltroSincroProps {
	readonly almacen: number,
	readonly setAlmacen: Setter<number>;

	readonly syncFilter: "todos" | "no-sincronizados";
	readonly setSyncFilter: Setter<"todos" | "no-sincronizados">;

	readonly stock: "conStock" | "todos";
	readonly setStock: Setter<"conStock" | "todos">;
}
function FiltroSincro(props: FiltroSincroProps) {

	// $("#comboAlmacenes").on("change", function () {
	// 	const xalmacen_id = $("#comboAlmacenes").val() as string;
	// 	void getStockArticulos(xalmacen_id);
	// });

	const { data, error, isPending } = useAlmacenes();

	if (error) return <QueryError error={error} fallback="Error cargando los almacenes." />;
	if (isPending) return <Loading />;



	return (
		<div style={{ padding: 30 }} role="tabpanel">
			<label>Almacen: </label>
			<select className="form-control" value={props.almacen} onChange={event => { props.setAlmacen(Number(event.target.value)); }}>
				{data.map((almacen) => (
					<option key={almacen.id} value={almacen.id}>{almacen.id} - {almacen.nombre}</option>
				))}
			</select>

			<br />

			<label>Sincronizados: </label>
			<select className="form-control" value={props.syncFilter} onChange={event => { props.setSyncFilter(event.target.value as "todos" | "no-sincronizados"); }}>
				<option value="no-sincronizados">No Sincronizados</option>
				<option value="todos">Todos</option>
			</select>

			<br />

			<label>Stock: </label>
			<select className="form-control" value={props.stock} onChange={event => { props.setStock(event.target.value as "conStock" | "todos"); }}>
				<option value="conStock">Con Stock</option>
				<option value="todos">Todos</option>
			</select>
		</div>
	);
}

export function useStockAlmacen(idAlmacen: number) {
	return useQuery<LineaStocksArticulo[]>({
		queryKey: ["getStockAlmacen", idAlmacen],
		queryFn: () => {
			const url = "/api/v1/getStockAlmacen";
			return sendAjaxRequest<LineaStocksArticulo[]>("GET", url, { "idAlmacen": idAlmacen });
		},
	});
}
export function useSyncArticulo() {
	const queryClient = useQueryClient();
	interface Params { prioritaria: DBPrioritaria; idArticulo: string; lote: string; idAlmacen: number; }

	const optimisticUpdate = (data: Params) => {
		const prevData = queryClient.getQueryData<LineaStocksArticulo[]>(["getStockAlmacen", data.idAlmacen]);
		if (!prevData) return false;
		const newData = prevData.map(item => {
			if (item.idArticulo != data.idArticulo) return item;

			if (data.prioritaria === "EKON") return { ...item, stockMySQL: item.stockERP };
			else return { ...item, stockERP: item.stockMySQL };

		});
		queryClient.setQueryData(["getStockAlmacen", data.idAlmacen], newData);
		return true;
	};

	return useMutation({
		mutationFn: async (data: Params) => {
			const updated = optimisticUpdate(data);
			const res = await sendAjaxRequest<RespuestaSync>("POST", "/api/v1/sincronizarAlmacen", data);

			//No he podido hacer la actualización optimista, asi que recargamos a la vieja usanza
			if (!updated) {
				await queryClient.invalidateQueries({ queryKey: ["getStockAlmacen", data.idAlmacen] });
			}

			if (res.status != "success") throw new Error(res.message ?? "Error desconocido al sincronizar el artículo");
			return res;
		},
		onError: (error, data) => {
			void queryClient.invalidateQueries({ queryKey: ["getStockAlmacen", data.idAlmacen] });
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["getLogsCab"] });
		},
	});
}

export function useAlmacenes() {
	return useQuery<Almacen[]>({
		queryKey: ["getAlmacenes"],
		queryFn: () => {
			return sendAjaxRequest<Almacen[]>("GET", "/api/v1/getAlmacenes");
		},
	});
}


export type DBPrioritaria = "EKON" | "MYSQL";

export interface LineaStocksArticulo {
	idArticulo: string;
	nombreArticulo: string;
	gestionaLotes: boolean;
	lote: string;
	stockERP: number | null;
	stockMySQL: number | null;
}
export interface Almacen {
	id: number,
	nombre: string;
}

export interface RespuestaSync {
	status: "success" | "error";
	message?: string;
}